import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ExternalLink, PlayCircle } from 'lucide-react';

/* Load the YouTube IFrame API once and share the promise. */
let ytApiPromise = null;
function loadYouTubeAPI() {
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve(window.YT);
    };
    if (!document.getElementById('yt-iframe-api')) {
      const s = document.createElement('script');
      s.id = 'yt-iframe-api';
      s.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(s);
    }
  });
  return ytApiPromise;
}

export default function Module01({ data }) {
  const { video_url, title, duration_min, clips = [], theme, playlist } = data;
  const isReel = !video_url && playlist && clips.some((c) => c.video_id);

  return (
    <div className="space-y-5">
      {video_url ? (
        <div className="overflow-hidden aspect-video border border-white/10">
          <video src={video_url} controls className="w-full h-full" poster={data.thumbnail_url} />
        </div>
      ) : isReel ? (
        <ReelPlayer clips={clips} poster={data.thumbnail_url} />
      ) : (
        <div className="overflow-hidden aspect-video border border-white/10">
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-white/3">
            <PlayCircle size={36} className="text-white/20" />
            <p className="text-[9px] tracking-[0.2em] uppercase text-gray-600">Reel generates at 2 AM</p>
          </div>
        </div>
      )}

      <div className="border-b border-white/8 pb-5">
        <p className="font-medium tracking-wide">{title || 'Morning Motivation'}</p>
        <div className="flex gap-3 mt-2 flex-wrap items-center">
          <span className="text-[10px] tracking-[0.15em] uppercase text-gray-500">
            {duration_min} min{video_url ? ' compiled' : ''}
          </span>
          {theme && (
            <span className="text-[9px] tracking-[0.15em] uppercase border px-2 py-0.5"
                  style={{ borderColor: 'var(--gold)', color: 'var(--gold)' }}>
              {theme}
            </span>
          )}
          {data.ranked_by && (
            <span className="text-[9px] tracking-[0.15em] uppercase text-gray-600 border border-white/10 px-2 py-0.5">
              curated by {data.ranked_by === 'claude' ? 'Claude' : 'ranking'}
            </span>
          )}
        </div>
        {data.generated_at && (
          <p className="text-[9px] tracking-[0.15em] uppercase text-gray-600 mt-2">
            Built {new Date(data.generated_at).toLocaleString()}
          </p>
        )}
      </div>

      {clips.length > 0 && !isReel && (
        <div>
          <p className="text-[9px] tracking-[0.3em] uppercase text-gray-600 mb-3">Included clips</p>
          {clips.map((clip, i) => <ClipRow key={i} clip={clip} index={i + 1} />)}
        </div>
      )}
    </div>
  );
}

/* ── Continuous reel: plays each clip's segment, advances automatically ── */

function ReelPlayer({ clips, poster }) {
  const mountRef = useRef(null);
  const playerRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [started, setStarted] = useState(false);
  const [ready, setReady] = useState(false);
  const indexRef = useRef(0);

  useEffect(() => { indexRef.current = index; }, [index]);

  const advance = useCallback(() => {
    const next = indexRef.current + 1;
    if (next >= clips.length) return;          // reel finished — leave the last frame up
    const c = clips[next];
    setIndex(next);
    playerRef.current?.loadVideoById({
      videoId: c.video_id,
      startSeconds: c.start || 0,
      endSeconds: c.end || undefined,
    });
  }, [clips]);

  // Build the player only once the user has opted in — autoplay with sound is
  // blocked until there's a gesture, and a muted first clip is worse than a
  // tap to start.
  useEffect(() => {
    if (!started) return;
    let cancelled = false;

    loadYouTubeAPI().then((YT) => {
      if (cancelled || !mountRef.current) return;
      const first = clips[0];
      playerRef.current = new YT.Player(mountRef.current, {
        host: 'https://www.youtube-nocookie.com',
        videoId: first.video_id,
        playerVars: {
          start: first.start || 0,
          end: first.end || undefined,
          autoplay: 1, rel: 0, modestbranding: 1, playsinline: 1,
        },
        events: {
          onReady: () => setReady(true),
          onStateChange: (e) => { if (e.data === YT.PlayerState.ENDED) advance(); },
          onError: () => advance(),   // private/geo-blocked clip — skip it
        },
      });
    });

    return () => {
      cancelled = true;
      try { playerRef.current?.destroy(); } catch (_) { /* already gone */ }
      playerRef.current = null;
    };
  }, [started, clips, advance]);

  const jumpTo = (i) => {
    if (!started) { setIndex(i); setStarted(true); return; }
    setIndex(i);
    playerRef.current?.loadVideoById({
      videoId: clips[i].video_id,
      startSeconds: clips[i].start || 0,
      endSeconds: clips[i].end || undefined,
    });
  };

  const total = clips.reduce((s, c) => s + (c.duration || 0), 0);

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden aspect-video border border-white/10 bg-black">
        {started ? (
          <div ref={mountRef} className="w-full h-full" />
        ) : (
          <button onClick={() => setStarted(true)} className="group w-full h-full relative">
            {poster && (
              <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />
            )}
            <div className="relative w-full h-full flex flex-col items-center justify-center gap-3">
              <PlayCircle size={44} style={{ color: 'var(--gold)' }} className="group-active:scale-95 transition-transform" />
              <p className="text-[9px] tracking-[0.25em] uppercase text-gray-300">
                Play reel · {clips.length} clips · {Math.round(total / 60)} min
              </p>
            </div>
          </button>
        )}
        {started && !ready && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-[9px] tracking-[0.25em] uppercase text-gray-500">Loading…</p>
          </div>
        )}
      </div>

      {/* Segment progress */}
      <div className="flex gap-1">
        {clips.map((_, i) => (
          <div key={i} className="h-0.5 flex-1 transition-colors"
               style={{ background: i <= index && started ? 'var(--gold)' : 'rgba(255,255,255,0.12)' }} />
        ))}
      </div>

      <div>
        <p className="text-[9px] tracking-[0.3em] uppercase text-gray-600 mb-3">
          Clip {started ? index + 1 : 1} of {clips.length} — tap to jump
        </p>
        {clips.map((clip, i) => (
          <ClipRow key={i} clip={clip} index={i + 1}
                   isActive={started && i === index}
                   onSelect={() => jumpTo(i)} />
        ))}
      </div>
    </div>
  );
}

function ClipRow({ clip, index, isActive, onSelect }) {
  const secs = clip.duration || 0;
  const mins = Math.floor(secs / 60);
  const duration = mins > 0 ? `${mins}m ${secs % 60}s` : `${secs}s`;

  return (
    <div
      onClick={onSelect}
      className={`flex items-center justify-between py-4 border-b border-white/8 transition-all
        ${onSelect ? 'cursor-pointer' : ''}
        ${isActive ? 'opacity-100' : 'opacity-60 hover:opacity-90'}`}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <span className="text-xs w-4 shrink-0 text-right font-light"
              style={{ color: isActive ? 'var(--gold)' : '#555' }}>
          {index}
        </span>
        <div className="min-w-0">
          <p className={`text-sm font-medium truncate tracking-wide ${isActive ? 'text-white' : 'text-gray-300'}`}>
            {clip.title}
          </p>
          <p className="text-[10px] tracking-[0.1em] uppercase text-gray-600 mt-0.5">{clip.channel}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-[10px] text-gray-600">{duration}</span>
        {clip.url && (
          <a href={clip.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
             className="text-gray-700 hover:text-white transition-colors">
            <ExternalLink size={12} />
          </a>
        )}
      </div>
    </div>
  );
}
